import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, message, Tag, Spin, Button, Modal, Form, Input, Switch, Space, Popconfirm, Select } from 'antd';
import { UserOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';
import { tradeAPI } from '../services/api';

const { Title } = Typography;
const { Option } = Select;

const TraderProfiles = () => {
  const [traderProfiles, setTraderProfiles] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingTraderProfile, setEditingTraderProfile] = useState(null);
  const [deletingTraderProfile, setDeletingTraderProfile] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [form] = Form.useForm();

  useEffect(() => {
    fetchTraderProfiles();
    fetchDataSources();
  }, []);

  useEffect(() => {
    fetchTraderProfiles();
  }, [pagination.current, pagination.pageSize]);

  const fetchTraderProfiles = async () => {
    try {
      const response = await tradeAPI.getTraderProfiles();
      const allData = response.data || [];
      const total = allData.length;

      // 前端分页计算
      const startIndex = (pagination.current - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const currentPageData = allData.slice(startIndex, endIndex);

      setTraderProfiles(currentPageData);
      setPagination((prev) => ({ ...prev, total }));
    } catch (error) {
      message.error('获取交易员资料失败');
      console.error('获取交易员资料失败:', error);
      setTraderProfiles([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleTableChange = (paginationConfig) => {
    setPagination({
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
      total: paginationConfig.total,
    });
  };

  const fetchDataSources = async () => {
    try {
      const response = await tradeAPI.getDataSources();
      setDataSources(response.data);
    } catch (error) {
      message.error('获取数据源失败');
      console.error('获取数据源失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTraderProfile(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingTraderProfile(record);
    form.setFieldsValue({
      source_id: record.source_id,
      display_name: record.display_name,
      profile_url: record.profile_url,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record) => {
    setDeletingTraderProfile(record);
    setIsDeleteModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingTraderProfile) return;

    try {
      await tradeAPI.deleteTraderProfile(deletingTraderProfile.id);
      message.success('交易员资料删除成功');
      setIsDeleteModalVisible(false);
      setDeletingTraderProfile(null);
      fetchTraderProfiles();
    } catch (error) {
      message.error('删除交易员资料失败');
      console.error('删除交易员资料失败:', error);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalVisible(false);
    setDeletingTraderProfile(null);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();

      if (editingTraderProfile) {
        // 更新交易员资料
        await tradeAPI.updateTraderProfile(editingTraderProfile.id, values);
        message.success('交易员资料更新成功');
      } else {
        // 创建交易员资料
        await tradeAPI.createTraderProfile(values);
        message.success('交易员资料创建成功');
      }

      setIsModalVisible(false);
      form.resetFields();
      fetchTraderProfiles();
    } catch (error) {
      message.error(error.response?.data?.detail || '操作失败');
      console.error('操作失败:', error);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const getDataSourceName = (sourceId) => {
    const dataSource = dataSources.find((ds) => ds.id === sourceId);
    return dataSource ? dataSource.name : '未知数据源';
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '数据源',
      dataIndex: 'source_id',
      key: 'source_id',
      render: (sourceId) => getDataSourceName(sourceId),
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      render: (displayName) => displayName || '-',
    },
    {
      title: '资料URL',
      dataIndex: 'profile_url',
      key: 'profile_url',
      render: (profileUrl) =>
        profileUrl ? (
          <a href={profileUrl} target="_blank" rel="noopener noreferrer">
            查看资料
          </a>
        ) : (
          '-'
        ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => moment.utc(date).utcOffset(8).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="middle">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除" description={`确定要删除交易员资料 "${record.display_name}" 吗？`} onConfirm={() => handleDelete(record)} okText="确认删除" cancelText="取消" okType="danger">
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <Title level={3}>交易员资料管理</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          新建交易员资料
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={traderProfiles}
        rowKey="id"
        onChange={handleTableChange}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `显示 ${range[0]}-${range[1]} 条，共 ${total} 条`,
        }}
      />

      <Modal title={editingTraderProfile ? '编辑交易员资料' : '新建交易员资料'} open={isModalVisible} onOk={handleModalOk} onCancel={handleModalCancel}>
        <Form form={form} layout="vertical">
          <Form.Item name="source_id" label="数据源" rules={[{ required: true, message: '请选择数据源' }]}>
            <Select placeholder="请选择数据源">
              {dataSources.map((dataSource) => (
                <Option key={dataSource.id} value={dataSource.id}>
                  {dataSource.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="display_name" label="显示名称">
            <Input placeholder="请输入交易员的显示名称 (可选)" />
          </Form.Item>

          <Form.Item name="profile_url" label="资料URL">
            <Input placeholder="请输入交易员资料页面的URL (可选)" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="确认删除" open={isDeleteModalVisible} onOk={handleConfirmDelete} onCancel={handleCancelDelete} okText="确认删除" cancelText="取消" okType="danger">
        <p>确定要删除交易员资料 "{deletingTraderProfile?.display_name || `交易员 ${deletingTraderProfile?.id}`}" 吗？</p>
        <p style={{ color: 'red', marginTop: '8px' }}>此操作无法撤销，删除后将永久失去该交易员资料及其所有相关数据。</p>
      </Modal>
    </div>
  );
};

export default TraderProfiles;
